"use client";

import classNames from "classnames";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { Form, Formik } from "formik";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { FC, useEffect, useState } from "react";
import OtpInput from "react-otp-input";
import { object, string } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button, Typography } from "@/shared/ui";

import styles from "./code-verification-form.module.scss";
import { CodeVerificationFormScheme } from "./code-verification-form.types";
import { useRequestResetPassword } from "./hooks/request-reset-password.hook";
import { useVerifyResetPassword } from "./hooks/verify-reset-password.hook";

dayjs.extend(duration);

const validationScheme = object({
  code: string().min(4),
});

const CodeVerificationForm: FC = () => {
  const searchParams = useSearchParams();

  const { mutate: verifyMutate, isPending } = useVerifyResetPassword();
  const { mutate: requestCode } = useRequestResetPassword();

  const email = searchParams.get("email");

  const [resendTimer, setResendTimer] = useState<number | null>(120000);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (resendTimer) {
      setIsResendDisabled(true);
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev && prev > 1000) {
            return prev - 1000;
          } else {
            if (timer) clearInterval(timer);
            setIsResendDisabled(false);
            return null;
          }
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendTimer]);

  const onSubmitHandler = ({ code }: CodeVerificationFormScheme) => {
    if (email) {
      verifyMutate({ code, email });
    }
  };

  const onResendHandler = async () => {
    if (email) {
      requestCode({ email }, { onSuccess: ({ waitMs }) => setResendTimer(waitMs) });
    }
  };

  const formatTime = (ms: number) => {
    const duration = dayjs.duration(ms);
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    return `${minutes > 0 ? `${minutes}m ` : ""}${seconds}s`;
  };

  return (
    <div className={styles.codeVerificationForm}>
      <div className={styles.header}>
        <Typography bold variant="large-title">
          Password Reset
        </Typography>
        <Typography variant="caption-1">We already sent a code to {email}</Typography>
      </div>

      <Formik<CodeVerificationFormScheme>
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={{ code: "" }}
        validationSchema={toFormikValidationSchema(validationScheme)}
        onSubmit={onSubmitHandler}
      >
        {({ values, setFieldValue }) => (
          <Form className={styles.form}>
            <OtpInput
              shouldAutoFocus
              value={values.code}
              inputType="number"
              numInputs={4}
              containerStyle={styles.otp}
              renderInput={(props, index) => (
                <input
                  {...props}
                  className={classNames(styles.codeInput, props.value && styles.filled)}
                  onKeyDown={(e) => {
                    if (e.key === "Backspace" && e.currentTarget.value) {
                      e.preventDefault();

                      const replacedNumWithSpace = values.code.split("").toSpliced(index, 1, " ").join("");
                      setFieldValue("code", replacedNumWithSpace);
                    } else {
                      props.onKeyDown(e);
                    }
                  }}
                />
              )}
              onChange={(value) => setFieldValue("code", value)}
            />
            <Button
              fullWidth
              size="big"
              type="submit"
              className={styles.submitButton}
              disabled={isPending || values.code.length < 4}
            >
              {"Continue"}
            </Button>
            <div className={styles.footer}>
              <Link href={"/auth/login"} className={styles.backButton}>
                <Typography variant="caption-1" align="center">
                  Back to Log In
                </Typography>
              </Link>

              <div className={styles.resend}>
                <Typography variant="caption-1" align="center">
                  Didn&apos;t receive the code?
                </Typography>
                <button type="button" disabled={isResendDisabled} onClick={onResendHandler}>
                  <Typography
                    variant="caption-1"
                    align="center"
                    className={classNames(styles.btn, isResendDisabled && styles.disabled)}
                  >
                    {resendTimer ? `Resend in ${formatTime(resendTimer)}` : "Resend"}
                  </Typography>
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export { CodeVerificationForm };
